import React, { useState, useEffect } from 'react';
import { Video, Users, Tv, Play, Settings, TrendingUp, MapPin, Mail, ExternalLink, Trophy } from 'lucide-react';
import { api } from '@/api';
import { resolveMediaUrl } from '@/utils/helpers';
import { PageHeroBanner } from '@/components/common/PageHeroBanner';
import { SEO } from '@/components/common/SEO';

// Partners Section Component
const PartnersSection = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPartners = async () => {
      try {
        const res = await api.getPartners(true);
        setPartners(res.data);
      } catch (error) {
        console.error('Error loading partners:', error);
      } finally {
        setLoading(false);
      }
    };
    loadPartners();
  }, []);

  const getColorClasses = (color) => {
    const colors = {
      sky: { bg: 'bg-sky-500/20', border: 'hover:border-sky-500/50', text: 'text-sky-400' },
      green: { bg: 'bg-green-500/20', border: 'hover:border-green-500/50', text: 'text-green-400' },
      red: { bg: 'bg-red-500/20', border: 'hover:border-red-500/50', text: 'text-red-400' },
      purple: { bg: 'bg-purple-500/20', border: 'hover:border-purple-500/50', text: 'text-purple-400' },
      yellow: { bg: 'bg-yellow-500/20', border: 'hover:border-yellow-500/50', text: 'text-yellow-400' },
      pink: { bg: 'bg-pink-500/20', border: 'hover:border-pink-500/50', text: 'text-pink-400' },
      orange: { bg: 'bg-orange-500/20', border: 'hover:border-orange-500/50', text: 'text-orange-400' },
    };
    return colors[color] || colors.sky;
  };

  if (loading) {
    return (
      <div id="partners" className="mb-16">
        <h2 className="text-3xl font-bold text-white mb-8 text-center">Our Partners & Affiliations</h2>
        <div className="flex justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-sky-400 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  if (partners.length === 0) {
    return null;
  }

  return (
    <div id="partners" className="mb-16">
      <h2 className="text-3xl font-bold text-white mb-8 text-center">Our Partners & Affiliations</h2>
      <div className={`grid md:grid-cols-2 ${partners.length >= 4 ? 'lg:grid-cols-4' : partners.length === 3 ? 'lg:grid-cols-3' : ''} gap-6`}>
        {partners.map(partner => {
          const colorClasses = getColorClasses(partner.color);
          return (
            <div 
              key={partner.id} 
              className={`bg-gray-800/50 rounded-xl p-6 border border-gray-700 ${colorClasses.border} transition-colors text-center`}
            >
              {partner.logo ? (
                <div className={`w-16 h-16 ${colorClasses.bg} rounded-full flex items-center justify-center mx-auto mb-4 p-2`}>
                  <img src={resolveMediaUrl(partner.logo)} alt={partner.name} className="w-full h-full object-contain" />
                </div>
              ) : (
                <div className={`w-16 h-16 ${colorClasses.bg} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <Trophy className={`w-8 h-8 ${colorClasses.text}`} />
                </div>
              )}
              <h3 className="text-lg font-semibold text-white mb-2">{partner.name}</h3>
              <p className="text-gray-400 text-sm">{partner.description}</p>
              {partner.website && (
                <a 
                  href={partner.website} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className={`inline-flex items-center gap-1 mt-3 text-sm ${colorClasses.text} hover:underline`}
                >
                  Visit Website <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Sports Covered */}
      <div className="mt-8 bg-gray-800/30 rounded-xl p-6 border border-gray-700/50">
        <h3 className="text-lg font-semibold text-white mb-4 text-center">Sports We Cover</h3>
        <div className="flex flex-wrap justify-center gap-3">
          <span className="px-4 py-2 bg-green-500/10 text-green-400 rounded-lg text-sm border border-green-500/20">Soccer</span>
          <span className="px-4 py-2 bg-green-500/10 text-green-400 rounded-lg text-sm border border-green-500/20">Gaelic Football</span>
          <span className="px-4 py-2 bg-green-500/10 text-green-400 rounded-lg text-sm border border-green-500/20">Hurling</span>
          <span className="px-4 py-2 bg-green-500/10 text-green-400 rounded-lg text-sm border border-green-500/20">Camogie</span>
          <span className="px-4 py-2 bg-green-500/10 text-green-400 rounded-lg text-sm border border-green-500/20">Rugby</span>
          <span className="px-4 py-2 bg-sky-500/10 text-sky-400 rounded-lg text-sm border border-sky-500/20">+ More</span>
        </div>
      </div>
    </div>
  );
};

export const AboutPage = () => {
  return (
    <div>
      <SEO 
        title="About Us"
        description="New Logical Sports is a broadcasting and live streaming service dedicated to providing high-quality video for grassroots amateur and semi-professional sports."
        url="/about"
      />
      <PageHeroBanner 
        pageSlug="about" 
        fallbackTitle="About New Logical Sports"
        fallbackSubtitle="Unlocking the potential of grassroots sports through high-quality broadcasting"
      />
      
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Mission Statement */}
        <div className="bg-gradient-to-r from-sky-900/30 to-blue-900/30 rounded-2xl p-8 mb-16 border border-sky-800/30">
          <h2 className="text-2xl font-bold text-white mb-4">Our Mission</h2>
          <p className="text-gray-300 text-lg leading-relaxed">
            New Logical Sports is a broadcasting and live streaming service dedicated to providing high-quality video 
            for the underserved world of grassroots amateur and semi-professional sports. We deliver professional-level 
            production including live coverage, highlights, and on-demand streaming at responsible rates.
          </p>
        </div>
        
        {/* About New Logical Sports */}
        <div id="about-nls" className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">About New Logical Sports</h2>
          <div className="bg-gray-800/50 rounded-xl p-8 border border-gray-700">
            <p className="text-gray-300 text-lg leading-relaxed">
              New Logical Sports is one of America's highest quality sports broadcasting and live streaming services for grassroots amateur and semi-professional sports teams. Launched in 2019 as a new service by parent company New Logical, NL Sports quickly established themselves as the premier option for clubs and leagues across the east coast of the USA.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mt-4">
              New Logical has covered regional and national finals in soccer, rugby, Gaelic football, and hurling/camogie, especially the 2021 and 2023 National Amateur Cup Finals, the 2021 and 2023 EPSL Finals, the 2024 NISA Nation National Final, every major USASA Region I final since 2021 and every major NY GAA and affiliated committees' county finals since 2023.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mt-4">
              New Logical has also collaborated and partnered with professional organizations in Major League Soccer & United Soccer League such as the Philadelphia Union, New York City FC, and Hartford Athletic to provide broadcasts for the academies, NYPD vs. FDNY, and the prestigious U.S. Open Cup.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mt-4">
              The streams are highly regarded and covered by notable soccer and GAA outlets as one of the highest quality video streams they have seen on the internet. Approved as a sanctioned U.S. Soccer broadcaster for U.S. Open Cup, New Logical Sports receives cross-promotion from the USSF for marquee Cup matches involving the nations' strongest amateur and professional clubs.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mt-4">
              In 2024, New Logical recorded record viewership for livestreams and highlights, reaching <span className="text-sky-400 font-semibold">4.5 million views</span> across audiences of over <span className="text-sky-400 font-semibold">400,000 users/devices</span> on social media.
            </p>
          </div>
        </div>
        
        {/* Services Grid */}
        <div id="services" className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">Our Services</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Single Angle Setup */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-sky-500/50 transition-colors">
              <div className="w-12 h-12 bg-sky-500/20 rounded-lg flex items-center justify-center mb-4">
                <Video className="w-6 h-6 text-sky-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Single Angle Setup</h3>
              <p className="text-gray-400 mb-4">Perfect for regular season games and smaller events.</p>
              <ul className="text-sm text-gray-400 space-y-1">
                <li>• Single camera coverage</li>
                <li>• Live commentator</li>
                <li>• Real-time score graphics</li>
                <li>• Multi-platform streaming</li>
              </ul>
            </div>
            
            {/* Professional Commentary Setup */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-sky-500/50 transition-colors">
              <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Professional Commentary</h3>
              <p className="text-gray-400 mb-4">Enhanced coverage with experienced broadcasters.</p>
              <ul className="text-sm text-gray-400 space-y-1">
                <li>• Professional commentator</li>
                <li>• Sideline reporter</li>
                <li>• Pre & post-game interviews</li>
                <li>• Audio-Technica quality audio</li>
              </ul>
            </div>
            
            {/* 4K Broadcast Quality */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-sky-500/50 transition-colors">
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center mb-4">
                <Tv className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">4K Broadcast Quality</h3>
              <p className="text-gray-400 mb-4">Major network quality for championship events.</p>
              <ul className="text-sm text-gray-400 space-y-1">
                <li>• Sony professional cameras</li>
                <li>• Up to 4K resolution</li>
                <li>• Professional commentator</li>
                <li>• Sideline reporting</li>
              </ul>
            </div>
            
            {/* Full Multi-Camera Setup */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-yellow-500/50 transition-colors relative overflow-hidden">
              <div className="absolute top-3 right-3 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                Premium
              </div>
              <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center mb-4">
                <Play className="w-6 h-6 text-yellow-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Full Multi-Camera Setup</h3>
              <p className="text-gray-400 mb-4">Complete broadcast production for major events.</p>
              <ul className="text-sm text-gray-400 space-y-1">
                <li>• 3-4 camera angles (tight, wide, field level)</li>
                <li>• Instant replay capability</li>
                <li>• Full graphics package</li>
                <li>• 15-min pregame show</li>
                <li>• Halftime & postgame coverage</li>
              </ul>
              <p className="text-xs text-gray-500 mt-3">*Available in New York area</p>
            </div>
            
            {/* Remote Graphics & Commentary */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-sky-500/50 transition-colors">
              <div className="w-12 h-12 bg-pink-500/20 rounded-lg flex items-center justify-center mb-4">
                <Settings className="w-6 h-6 text-pink-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Remote Studio Services</h3>
              <p className="text-gray-400 mb-4">Enhance your existing stream with our graphics and commentary.</p>
              <ul className="text-sm text-gray-400 space-y-1">
                <li>• RTMP feed integration</li>
                <li>• Professional scoreboard overlay</li>
                <li>• Video quality consulting</li>
                <li>• Remote commentary option</li>
              </ul>
            </div>
            
            {/* Performance Analysis */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-sky-500/50 transition-colors">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Performance Analysis</h3>
              <p className="text-gray-400 mb-4">Partner with Elite Performance Systems for athlete development.</p>
              <ul className="text-sm text-gray-400 space-y-1">
                <li>• Video analysis tools</li>
                <li>• Stat tracking</li>
                <li>• Post-game analysis</li>
                <li>• Player dashboards</li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Platforms Section */}
        <div id="platforms" className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">Our Platforms</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {/* New Logical Sports+ */}
            <div className="bg-gradient-to-br from-sky-900/40 to-blue-900/40 rounded-xl p-6 border border-sky-700/50">
              <h3 className="text-xl font-semibold text-white mb-3">New Logical Sports+</h3>
              <p className="text-gray-300 mb-4">
                Our subscription platform for live matches and archived events. Available on mobile and smart TV with annual pass options.
              </p>
              <a 
                href="https://plus.newlogicalsports.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 transition-colors"
              >
                Visit NLS+ <ExternalLink className="w-4 h-4" />
              </a>
            </div>
            
            {/* Distribution Platforms */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <h3 className="text-xl font-semibold text-white mb-3">Multi-Platform Distribution</h3>
              <p className="text-gray-300 mb-4">We stream to all major platforms simultaneously:</p>
              <div className="flex flex-wrap gap-3">
                <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm">YouTube</span>
                <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm">Facebook</span>
                <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm">Twitch</span>
                <span className="px-3 py-1 bg-gray-500/20 text-gray-400 rounded-full text-sm">X (Twitter)</span>
              </div>
            </div>
            
            {/* New Logical TV */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <h3 className="text-xl font-semibold text-white mb-3">New Logical TV</h3>
              <p className="text-gray-300">
                Our 24/7 IPTV network featuring major livestreams and archived content. Perfect for venues and sports bars.
              </p>
            </div>
            
            {/* New Logical Radio */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <h3 className="text-xl font-semibold text-white mb-3">New Logical Radio</h3>
              <p className="text-gray-300">
                Audio-only feeds for livestreams - ideal for fans on the go. Live commentary available for budget-conscious clubs.
              </p>
            </div>
          </div>
        </div>
        
        {/* Coverage Areas */}
        <div id="coverage" className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">Coverage Areas</h2>
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Primary Markets</h3>
                <ul className="text-gray-300 space-y-2">
                  <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-sky-400" /> New York City</li>
                  <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-sky-400" /> Long Island</li>
                  <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-sky-400" /> Northern New Jersey</li>
                  <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-sky-400" /> Central New Jersey</li>
                  <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-sky-400" /> Philadelphia</li>
                  <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-sky-400" /> Boston</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Expanding Markets</h3>
                <ul className="text-gray-300 space-y-2">
                  <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-yellow-400" /> Washington, D.C. (via DMV Sports Media)</li>
                  <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-500" /> Canada</li>
                  <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-500" /> Ireland & UK</li>
                  <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-500" /> European Union</li>
                </ul>
                <p className="text-sm text-gray-500 mt-3">Investment & franchise opportunities available</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Partners Section */}
        <PartnersSection />
        
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          <div className="bg-gray-800/50 rounded-xl p-6 text-center border border-gray-700">
            <div className="text-3xl font-bold text-sky-400 mb-1">4.5M+</div>
            <div className="text-gray-400 text-sm">Total Views</div>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-6 text-center border border-gray-700">
            <div className="text-3xl font-bold text-sky-400 mb-1">400K+</div>
            <div className="text-gray-400 text-sm">Users in 2024</div>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-6 text-center border border-gray-700">
            <div className="text-3xl font-bold text-sky-400 mb-1">2019</div>
            <div className="text-gray-400 text-sm">Founded</div>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-6 text-center border border-gray-700">
            <div className="text-3xl font-bold text-sky-400 mb-1">10</div>
            <div className="text-gray-400 text-sm">Max Cameras</div>
          </div>
        </div>
        
        {/* Contact Section */}
        <div id="contact" className="bg-gradient-to-r from-sky-900/50 to-blue-900/50 rounded-2xl p-8 md:p-12">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Ready to Elevate Your Coverage?
            </h2>
            <p className="text-gray-300 mb-8">
              Contact us to discuss your event requirements and get a custom quote for your streaming or production needs.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <a 
                href="mailto:info@newlogical.com" 
                className="px-6 py-3 bg-sky-500 hover:bg-sky-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <Mail className="w-5 h-5" />
                info@newlogical.com
              </a>
              <a 
                href="https://newlogical.com" 
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <ExternalLink className="w-5 h-5" />
                newlogical.com
              </a>
            </div>
            
            {/* Social Links */}
            <div className="flex items-center justify-center gap-4">
              <a href="https://youtube.com/@newlogical" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-red-400 transition-colors">
                YouTube
              </a>
              <span className="text-gray-600">•</span>
              <a href="https://instagram.com/newlogicalsports" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-pink-400 transition-colors">
                Instagram
              </a>
              <span className="text-gray-600">•</span>
              <a href="https://www.facebook.com/people/New-Logical-Sports/100084489588017/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-400 transition-colors">
                Facebook
              </a>
              <span className="text-gray-600">•</span>
              <a href="https://x.com/newlogicaltech" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-300 transition-colors">
                X
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
